<template>
  <div>
    <div class="flex flex-wrap gap-2 justify-between items-start mb-3">
      <div>
        <div class="text-sm font-medium text-gray-900">
          {{ invoice.identifier }}
        </div>
        <div class="text-xs text-gray-500">{{ formatDate(invoice.date) }}</div>
        <div v-if="invoice.firm" class="mt-0.5 text-xs text-gray-400">
          {{ invoice.firm }}
        </div>
      </div>
      <span
        class="inline-flex px-2 py-0.5 text-xs font-semibold tracking-wide uppercase rounded"
        :class="
          isPaid
            ? 'text-green-800 bg-green-100'
            : 'text-amber-800 bg-amber-100'
        "
      >
        {{ isPaid ? "Paid" : "Payment due" }}
      </span>
    </div>

    <div class="space-y-1.5 text-sm">
      <div class="flex justify-between">
        <span class="text-gray-600">Bill</span>
        <span>{{ invoice.amount | currency }}</span>
      </div>
      <div class="flex justify-between">
        <span class="text-gray-600">Received</span>
        <span>{{ invoice.received | currency }}</span>
      </div>
      <div class="flex justify-between font-medium">
        <span class="text-gray-600">Balance</span>
        <span :class="isPaid ? 'text-green-600' : 'text-red-600'">
          {{ invoice.balance | currency }}
        </span>
      </div>
    </div>

    <div
      v-if="isPaid"
      class="p-2 mt-3 text-xs text-green-800 bg-green-50 rounded border border-green-200"
    >
      This bill is fully paid. No further payment needed on this side.
    </div>

    <template v-else-if="payableIndex >= 0">
      <div class="grid grid-cols-2 gap-2 mt-3">
        <div>
          <label class="block mb-1 text-xs font-medium text-gray-600"
            >Discount</label
          >
          <input
            :value="discountAmounts[payableIndex]"
            type="number"
            min="0"
            :max="invoice.balance"
            class="px-2 py-1.5 w-full text-sm text-right rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
            @focus="$event.target.select()"
            @input="
              $emit('update-discount', {
                index: payableIndex,
                value: $event.target.value,
                balance: invoice.balance
              })
            "
            placeholder="0"
          />
        </div>
        <div>
          <label class="block mb-1 text-xs font-medium text-gray-600"
            >Payment Amount</label
          >
          <input
            :value="invoicePaymentAmounts[payableIndex]"
            type="number"
            min="0"
            :max="maxPayment"
            class="px-2 py-1.5 w-full text-sm text-right rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
            @focus="$event.target.select()"
            @input="
              $emit('update-payment', {
                index: payableIndex,
                value: $event.target.value
              })
            "
            :placeholder="String(maxPayment)"
          />
          <div class="mt-1 text-xs text-right text-gray-500">
            Max: {{ maxPayment | currency }}
          </div>
        </div>
      </div>

      <!-- Per-invoice payment modes -->
      <div class="p-2.5 mt-3 bg-slate-50 rounded-lg border border-slate-200">
        <div class="mb-2">
          <span class="text-xs font-semibold tracking-wide text-slate-700">Payment Mode</span>
          <!-- Quick-select pills: one tap fills full amount into that mode -->
          <div class="flex flex-wrap gap-1.5 mt-1.5">
            <button
              v-for="mode in paymentModes"
              :key="mode.paymentModeId"
              type="button"
              :class="[
                'px-3 py-0.5 text-xs font-medium rounded-full border transition-colors',
                activeModeId === mode.paymentModeId
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-600'
              ]"
              @click="$emit('quick-fill-mode', { index: payableIndex, modeId: mode.paymentModeId })"
            >
              {{ mode.label }}
            </button>
          </div>
        </div>
        <!-- Partial amount inputs — focus selects all so you can type directly -->
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 mt-2">
          <div
            v-for="mode in paymentModes"
            :key="mode.paymentModeId"
            class="flex flex-col"
          >
            <label class="mb-0.5 text-xs text-gray-500">{{ mode.label }}</label>
            <input
              :value="modeAmount(mode.paymentModeId)"
              type="number"
              min="0"
              class="px-2 py-1 w-full text-sm text-right bg-white rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
              :class="modeAmount(mode.paymentModeId) > 0 ? 'border-blue-400' : 'border-gray-300'"
              @focus="$event.target.select()"
              @input="
                $emit('update-mode', {
                  index: payableIndex,
                  modeId: mode.paymentModeId,
                  value: $event.target.value
                })
              "
            />
          </div>
        </div>
        <div class="flex justify-between items-center mt-2 text-xs">
          <span class="text-gray-500">Modes total</span>
          <span
            class="font-semibold"
            :class="modesMatch ? 'text-green-600' : 'text-red-600'"
          >
            {{ modesTotal | currency }}
          </span>
        </div>
        <div
          v-if="!modesMatch && paymentAmount > 0"
          class="mt-1 text-xs text-red-600"
        >
          Must equal payment amount ({{ paymentAmount | currency }})
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "FirmInvoicePanel",
  props: {
    invoice: {
      type: Object,
      required: true
    },
    payableIndex: {
      type: Number,
      default: -1
    },
    discountAmounts: {
      type: Array,
      default: () => []
    },
    invoicePaymentAmounts: {
      type: Array,
      default: () => []
    },
    paymentModes: {
      type: Array,
      default: () => []
    },
    invoiceModeAmounts: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    isPaid() {
      return (parseFloat(this.invoice.balance) || 0) <= 0;
    },
    maxPayment() {
      if (this.payableIndex < 0) return 0;
      const balance = parseFloat(this.invoice.balance) || 0;
      const discount =
        parseFloat(this.discountAmounts[this.payableIndex]) || 0;
      return Math.max(0, balance - discount);
    },
    paymentAmount() {
      if (this.payableIndex < 0) return 0;
      return parseFloat(this.invoicePaymentAmounts[this.payableIndex]) || 0;
    },
    modesTotal() {
      return Object.values(this.invoiceModeAmounts || {}).reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
    },
    modesMatch() {
      if (this.paymentAmount <= 0) return this.modesTotal <= 0;
      return Math.abs(this.modesTotal - this.paymentAmount) < 0.01;
    },
    activeModeId() {
      const amounts = this.invoiceModeAmounts || {};
      const nonZero = Object.entries(amounts).filter(([, v]) => (parseFloat(v) || 0) > 0);
      if (nonZero.length === 1) return nonZero[0][0];
      return null;
    }
  },
  methods: {
    modeAmount(modeId) {
      const amounts = this.invoiceModeAmounts || {};
      return amounts[modeId] != null ? amounts[modeId] : 0;
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }
  },
  filters: {
    currency(value) {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR"
      }).format(amount);
    }
  }
};
</script>
